import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import { addMinutes, differenceInMinutes } from "date-fns";

import GlobalConfig from "app/config/global";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import { useLocation } from "react-router";
import { useLocationState } from "react-router-use-location-state";
import { useParams } from "react-router-dom";
import useRetainersDashboardNotes from "features/projects/retainers-dashboard/single/useRetainersDashboardNotes";

export const TIME_INTERVAL_IN_MIN = 5;

function reducer(oldState, action) {
    const { retainerID, dateTime, notes } = action.payload;
    let retainersAndNotesData = [];
    oldState.forEach((d) => retainersAndNotesData.push(d));

    const setRetainersNotesIcon = (retainerIdIndex = null) => {
        let newNotesData = [...notes];
        const retainerLastNoteVisitedLocalDateTime = retainerIdIndex
            ? retainersAndNotesData[retainerIdIndex]?.dateTime
            : dateTime;

        newNotesData.forEach((d, i) => {
            const dateDiffInMin = differenceInMinutes(
                addMinutes(
                    retainerLastNoteVisitedLocalDateTime,
                    new Date(d.Date).getTimezoneOffset()
                    /* Adding timezone offset here as local and server time is different */
                ),
                new Date(d.Date)
                /* Server time when note has been created */
            );

            d.isNoteVisited =
                retainersAndNotesData[retainerIdIndex]?.notesData[
                    i
                ]?.isNoteVisited;

            d.showNotificationIcon =
                dateDiffInMin < TIME_INTERVAL_IN_MIN &&
                dateDiffInMin >= 0 &&
                !retainersAndNotesData[retainerIdIndex]?.notesData[i]
                    ?.isNoteVisited;
        });

        return newNotesData;
    };

    if (action.type === "setRetainerNoteData") {
        const retainerIdIndex = retainersAndNotesData.findIndex(
            (f) => f.id === retainerID
        );
        if (retainerIdIndex === -1) {
            const newNotesData = setRetainersNotesIcon();

            retainersAndNotesData.push({
                id: retainerID,
                retainerDateTime: dateTime,
                notesData: setRetainersNotesIcon(),
                showNotificationIconForNotesTab: newNotesData?.some(
                    (s) => s.showNotificationIcon
                ),
                isNoteVisited: false,
            });
        } else if (retainerIdIndex > -1) {
            retainersAndNotesData[retainerIdIndex].id = retainerID;
            retainersAndNotesData[retainerIdIndex].retainerDateTime = dateTime;

            const newNotesData = setRetainersNotesIcon(retainerIdIndex);

            retainersAndNotesData[retainerIdIndex].notesData = newNotesData;

            retainersAndNotesData[
                retainerIdIndex
            ].showNotificationIconForNotesTab = newNotesData?.some(
                (s) => s.showNotificationIcon
            );
        }
        return retainersAndNotesData;
    } else if (action.type === "setAllNotesDataIconToFalse") {
        const retainerIdIndex = retainersAndNotesData.findIndex(
            (f) => f.id === retainerID
        );
        retainersAndNotesData[retainerIdIndex].notesData.forEach((d) => {
            d.showNotificationIcon = false;
            d.isNoteVisited = true;
        });
        retainersAndNotesData[
            retainerIdIndex
        ].showNotificationIconForNotesTab = false;

        return retainersAndNotesData;
    } else {
        throw new Error("Invalid Action Type");
    }
}

const RetainersDashboardLayoutContext = React.createContext({
    setRetainerNoteVistitedDateTime: () => void 0,
    setAllNotesDataIconToFalse: () => void 0,
});

export const RetainersDashboardLayoutContextProvider = (props) => {
    const { state } = useLocation();

    const { retainerId } = useParams();
    const sessionData = useContext(SessionContext);
    const location = useLocation();

    const [filters, setFilters] = useState();
    const cuurentRetainerId = useRef(retainerId);
    // To reset note type filter when retainer selector changes
    useEffect(() => {
        setFilters([]);
        cuurentRetainerId.current = retainerId;
    }, [retainerId]);

    const filterVal = filters?.find((f) => f.id === "TypeId")?.value ?? "";
    const noteTypeIdVal =
        cuurentRetainerId.current === retainerId ? filterVal : "";

    const { useRetainersNotesList } = useRetainersDashboardNotes();
    const notesList = useRetainersNotesList({
        retainerIdFE: retainerId,
        noteTypeId: noteTypeIdVal,
    });

    const pathNameIncludesNotes = location.pathname.includes(
        `/${GlobalConfig.routes.projectsRetainersDashboardNotesTab}`
    );

    const [initialState] = useState(() => {
        const initialStateData = [];
        let stateType = Array.isArray(state);
        if (state?.length !== 0 && stateType) {
            state?.forEach((data) => {
                initialStateData.push(data.key);
            });
        }
        return initialStateData;
    });

    const [retainerNoteTabVisitState, setRetainerNoteTabVisitState] =
        useLocationState("retainersAndNotesState", initialState);

    const [newReducerState, dispatch] = useReducer(
        reducer,
        retainerNoteTabVisitState
    );

    const [flagToLoad, setFlagToLoad] = useState(true);

    const setRetainerNoteData = useCallback((retainerID, dateTime, notes) => {
        dispatch({
            type: "setRetainerNoteData",
            payload: { retainerID, dateTime, notes },
        });
    }, []);

    const setAllNotesDataIconToFalse = useCallback((retainerID) => {
        dispatch({
            type: "setAllNotesDataIconToFalse",
            payload: { retainerID },
        });
    }, []);

    const getNotesTabIndicator = useCallback(
        (retainerID) =>
            newReducerState.find((f) => f.id === retainerID)
                ?.showNotificationIconForNotesTab,
        [newReducerState]
    );

    const getNotesData = useCallback(
        (retainerID) =>
            newReducerState.find((f) => f.id === retainerID)?.notesData,
        [newReducerState]
    );

    const checkIfNotesDataContainsIcon = useCallback(
        (retainerID) =>
            newReducerState
                .find((f) => f.id === retainerID)
                ?.notesData?.some((s) => s.showNotificationIcon),
        [newReducerState]
    );

    const [updatedNotesData, setUpdatedNotesData] = useState(
        getNotesData(retainerId) ?? []
    );
    
    const prevNotesListRef = useRef();
    
    useEffect(() => {
        const notesD = getNotesData(retainerId);
        setUpdatedNotesData(notesD);
    }, [retainerId, newReducerState, getNotesData]);

    useEffect(() => {
        if (!notesList.isFetching && notesList.data) {
            if (prevNotesListRef.current === notesList.data) {
                return;
            }
            
            prevNotesListRef.current = notesList.data;
            
            // To display edit/view icon require isCreatorOfNote
            notesList.data.forEach((d) => {
                d.isCreatorOfNote =
                    d.Creator === sessionData.fullName;
            });

            // Calcular valores directamente del state
            const retainerData = newReducerState.find((f) => f.id === retainerId);
            const noteVisitedDateTime = retainerData?.value;
            const currentNotesListLength = retainerData?.notesData?.length;

            // To store retainers related data
            const dateDiffInMin = differenceInMinutes(
                new Date(),
                noteVisitedDateTime
            );

            if (
                notesList.data.length >= 0 &&
                notesList.data.length !== currentNotesListLength
            ) {
                setFlagToLoad(true);
            }

            if (Number.isNaN(dateDiffInMin) && flagToLoad) {
                setFlagToLoad(false);
                setRetainerNoteData(
                    retainerId,
                    noteVisitedDateTime ??
                        new Date().toISOString(),
                    notesList.data
                );
            }
        }
    }, [notesList.isFetching, notesList.data, flagToLoad, retainerId, sessionData.fullName, setRetainerNoteData, newReducerState]);

    // When filter selected; to update notes data updating flag state here
    useEffect(() => {
        if (filters?.length > 0 && !flagToLoad) {
            setFlagToLoad(true);
        }
    }, [filters, flagToLoad]);

    // Remove indicator from table row
    const hasNotesDataIcon = checkIfNotesDataContainsIcon(retainerId);
    useEffect(() => {
        let timer = null;
        if (hasNotesDataIcon && pathNameIncludesNotes) {
            timer = setTimeout(() => {
                setAllNotesDataIconToFalse(retainerId);
            }, 30000); // 30000ms i.e. 30s
        }

        return () => {
            clearTimeout(timer);
        };
    }, [hasNotesDataIcon, pathNameIncludesNotes, retainerId, setAllNotesDataIconToFalse]); 

    useEffect(() => {
        setRetainerNoteTabVisitState(newReducerState);
    }, [newReducerState, setRetainerNoteTabVisitState]);

    const contextValue = useMemo(() => {
        return {
            getNotesTabIndicator,
            updatedNotesData,
            newReducerState,
            notesList,
            setFilters,
            setFlagToLoad,
        };
    }, [
        getNotesTabIndicator,
        updatedNotesData,
        newReducerState,
        notesList,
        setFilters,
        setFlagToLoad,
    ]);

    return (
        <RetainersDashboardLayoutContext.Provider value={contextValue}>
            {props.children}
        </RetainersDashboardLayoutContext.Provider>
    );
};

RetainersDashboardLayoutContextProvider.propTypes = {
    children: PropTypes.node
};

export default RetainersDashboardLayoutContext;
